<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="500"
    @click:outside="handleClose"
  >
    <v-card color="#2B2D33">
      <v-card-title class="d-flex align-center pa-4 text-white">
        <!-- <v-icon
          :icon="mode === 'new' ? 'mdi-cloud-upload' : 'mdi-file-replace'"
          class="mr-2"
          color="primary"
        /> -->
        {{ mode === 'new' ? (type === 'dwg' ? '上传CAD文件' : '上传图片') : (type === 'dwg' ? '替换CAD文件' : '替换图片') }}
      </v-card-title>

      <v-divider color="rgba(255, 255, 255, 0.12)" />

      <v-card-text class="pa-4">
        <!-- 上传区域 -->
        <div 
          class="upload-area"
          @click="$refs.fileInput.click()"
          @dragover.prevent
          @drop.prevent="handleDrop"
        >
        <!-- ,.dwg -->
          <input
            ref="fileInput"
            type="file"
            :accept="mode === 'replace' ? (type === 'dwg' ? '.dwg' : '.jpg,.jpeg,.png,.webp,.pdf') : ('.jpg,.jpeg,.png,.webp,.pdf')"
            :multiple="mode === 'new'"
            class="hidden-input"
            @change="handleFileChange"
          >
          
          <!-- <v-icon
            :icon="mode === 'new' ? 'mdi-image-multiple' : 'mdi-image'"
            color="primary"
            size="48"
          /> -->
          <img src="@/assets/uploadLogo.png" alt="上传" style="width: 20px;">
          <div class="text-h6">
            {{ files?.length ? '点击或拖拽更多图片' : '点击或拖拽上传图片' }}
          </div>
          
          <div class="text-body-2 text-medium-emphasis mt-2">
            <!-- 、DWG -->
            {{ mode === 'replace' ? (type === 'dwg' ? '支持 DWG 格式' : '支持 JPG、PNG、WebP、PDF 格式') : '支持 JPG、PNG、WebP、PDF 格式' }}
            <br>
            {{ mode === 'new' ? (type === 'dwg' ? '可同时上传多个CAD文件' : '可同时上传多张图片') : (type === 'dwg' ? '请选择一个CAD文件替换' : '请选择一张图片替换') }}
            <br>
            建议使用 Chrome、Edge、Safari 浏览器
          </div>
        </div>

        <!-- 文件列表 -->
        <v-list v-if="files?.length" class="file-list mt-4" color="#2B2D33">
          <v-list-item
            v-for="(file, index) in files"
            :key="index"
            class="file-item"
            color="#3A3B3F"
          >
            <template v-slot:prepend>
              <v-icon
                :color="isValidFileType(file) ? 'primary' : 'error'"
                :icon="isValidFileType(file) ? 'mdi-file-image' : 'mdi-file-alert'"
              />
            </template>

            <v-list-item-title>
              {{ file.name }}
            </v-list-item-title>

            <v-list-item-subtitle>
              {{ formatFileSize(file.size) }}
              <span v-if="!isValidFileType(file)" class="text-error">
                - 不支持的文件格式
              </span>
            </v-list-item-subtitle>

            <template v-slot:append>
              <v-btn
                icon="mdi-close"
                variant="text"
                size="small"
                @click="removeFile(index)"
              />
            </template>
          </v-list-item>
        </v-list>

       <!-- 上传进度 -->
       <div v-if="uploading" class="progress-container mt-4">
         <div class="d-flex align-start">
           <v-icon
             icon="mdi-cloud-upload"
             color="primary"
             class="mr-2"
           />
           <div class="progress-info">
             正在上传 {{ uploadedCount }}/{{ totalCount }} ({{ Math.round(uploadProgress) }}%))
           </div>
         </div>
       </div>

       <div v-if="converting && currentPdfName" class="progress-container mt-4">
         <div class="d-flex align-start">
           <v-icon
             icon="mdi-file-pdf"
             color="primary"
             class="mr-2"
           />
           <div class="progress-info">
             正在转换PDF "{{ currentPdfName }}" 第 {{ convertedPages }}/{{ totalPages }} 页
           </div>
         </div>
       </div>

       <!-- 等待转换提示 -->
       <div 
         v-if="waitingPdfNames.length > 0" 
         class="progress-container mt-4"
       >
         <div class="d-flex align-start">
           <v-icon
             icon="mdi-file-clock"
             color="warning"
             class="mr-2"
           />
           <div class="progress-info waiting-files">
             等待转换：{{ waitingPdfNames.join('、') }}
           </div>
         </div>
       </div>

        <!-- 错误提示 -->
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          class="mt-4"
          closable
          @click:close="error = null"
        >
          {{ error }}
        </v-alert>
      </v-card-text>

      <v-divider color="rgba(255, 255, 255, 0.12)" />

      <!-- 底部操作栏 -->
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          color="error"
          variant="tonal"
          :disabled="uploading"
          @click="handleClose"
          style="margin-right: 16px;"
        >
          取消
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="uploading"
          :disabled="!canUpload"
          class="ml-2"
          @click="handleUpload"
        >
          <template v-if="uploading">
            {{ uploadedCount }}/{{ totalCount }} ({{ Math.round(uploadProgress) }}%))
          </template>
          <template v-else>
            {{ mode === 'new' ? '开始上传' : '替换' }}
          </template>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import * as pdfjsLib from 'pdfjs-dist'
import { getPdfWorkerUrl } from '@/utils/pdfWorker'

// 设置 PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfWorkerUrl()

import { uploadApi } from '@/services/api'

const props = defineProps<{
  modelValue: boolean
  mode: 'new' | string
  type: 'image' | 'dwg'
  uploading?: boolean
  uploadProgress?: number
  error?: string | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'upload', urls: string[]): void
  (e: 'update:files', files: File[] | null): void
}>()

const fileInput = ref<HTMLInputElement | null>(null)
const files = ref<File[] | null>(null)
const uploading = ref(false)
const uploadProgress = ref(0)
const error = ref<string | null>(null)
const converting = ref(false)
const convertProgress = ref(0)
const totalPages = ref(0)
const convertedPages = ref(0)
const currentPdfName = ref('')
const processingQueue = ref<File[]>([])  
const isProcessing = ref(false)  
const uploadedCount = ref(0)  // 添加已上传数量计数器
const totalCount = ref(0)     // 添加总文件数计数器

const dialogVisible = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  }
})

// 获取等待转换的PDF文件名列表
const waitingPdfNames = computed(() => {
  // 从第二个文件开始，因为第一个正在转换
  return processingQueue.value
    .slice(1)  // 跳过第一个正在处理的文件
    .filter(file => file.type === 'application/pdf')
    .map(file => file.name)
})

// 是否可以上传
const canUpload = computed(() => {
  // 如果没有文件，不能上传
  if (!files.value?.length) return false
  // 如果是替换模式且有多个文件，不能上传
  if (props.mode === 'replace' && files.value.length > 1) return false
  // 如果有文件正在转换中，不能上传
  if (converting.value || processingQueue.value.length > 0) return false
  // 所有文件必须是有效的文件类型
  return files.value.every(file => isValidFileType(file))
})

// 验证文件类型
const isValidFileType = (file: File) => { 
  const validTypes = ['','image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'image/dwg']
  return validTypes.includes(file.type)
}

// 格式化文件大小
const formatFileSize = (size: number) => {
  if (size < 1024) return size + ' B'
  if (size < 1024 * 1024) return (size / 1024).toFixed(2) + ' KB'
  return (size / (1024 * 1024)).toFixed(2) + ' MB'
}

// PDF转图片函数
const convertPdfToImages = async (file: File): Promise<File[]> => {
  const imageFiles: File[] = []
  converting.value = true
  convertedPages.value = 0
  totalPages.value = 0
  currentPdfName.value = file.name
  
  try {
    const arrayBuffer = await file.arrayBuffer()
    const loadingTask = pdfjsLib.getDocument(arrayBuffer)
    const pdf = await loadingTask.promise
    totalPages.value = pdf.numPages
    
    for (let pageNum = 1; pageNum <= totalPages.value; pageNum++) {
      const page = await pdf.getPage(pageNum)
      const viewport = page.getViewport({ scale: 1.5 })
      
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.height = viewport.height
      canvas.width = viewport.width
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise
      
      const blob = await new Promise<Blob>(resolve => {
        canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.95)
      })
      
      const imageFile = new File([blob], `${file.name.replace('.pdf', '')}_${pageNum}.jpg`, {
        type: 'image/jpeg'
      })
      
      imageFiles.push(imageFile)
      convertedPages.value = pageNum
    }
    
    return imageFiles
  } catch (error) {
    console.error('PDF转换错误:', error)
    throw new Error('PDF文件转换失败')
  } finally {
    converting.value = false
    currentPdfName.value = ''
  }
}

// 处理队列中的文件
const processQueuedFiles = async () => {
  if (processingQueue.value.length === 0 || converting.value) {
    return
  }

  converting.value = true
  const pdfFile = processingQueue.value[0]
  currentPdfName.value = pdfFile.name

  try {
    // 转换PDF为图片
    const images = await convertPdfToImages(pdfFile)
    
    // 将转换后的图片添加到文件列表
    files.value = files.value.filter(f => f !== pdfFile) // 移除原始PDF
    files.value = [...files.value, ...images]
    
    // 从队列中移除已处理的PDF
    processingQueue.value.shift()
    
    // 重置转换状态
    converting.value = false
    currentPdfName.value = ''
    convertedPages.value = 0
    totalPages.value = 0

    // 继续处理队列中的下一个文件
    if (processingQueue.value.length > 0) {
      processQueuedFiles()
    }
  } catch (err) {
    console.error('PDF转换失败:', err)
    error.value = `PDF "${pdfFile.name}" 转换失败: ${err.message}`
    
    // 从队列和文件列表中移除失败的PDF
    processingQueue.value.shift()
    files.value = files.value.filter(f => f !== pdfFile)
    
    // 重置转换状态
    converting.value = false
    currentPdfName.value = ''
    convertedPages.value = 0
    totalPages.value = 0

    // 继续处理队列中的下一个文件
    if (processingQueue.value.length > 0) {
      processQueuedFiles()
    }
  }
}

// 添加文件到处理队列
const addToProcessingQueue = (newFiles: File[]) => {
  if (props.mode === 'replace') {
    // 替换模式下只保留最后选择的文件
    files.value = [newFiles[newFiles.length - 1]]
    processingQueue.value = []
  } else {
    // 新增模式下追加所有文件
    files.value = [...(files.value || []), ...newFiles]
  }

  // 将PDF文件添加到处理队列
  const pdfFiles = files.value.filter(file => file.type === 'application/pdf')
  processingQueue.value.push(...pdfFiles)
  
  // 如果有PDF文件且当前没有正在转换的文件，开始处理队列
  if (pdfFiles.length > 0 && !converting.value) {
    processQueuedFiles()
  }
}

// 处理文件改变
const handleFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files?.length) return
  
  try {
    // 将文件添加到处理队列
    addToProcessingQueue(Array.from(input.files))
  } finally {
    input.value = ''
  }
}

// 处理拖放
const handleDrop = async (event: DragEvent) => {
  if (!event.dataTransfer?.files.length) return
  
  // 将文件添加到处理队列
  addToProcessingQueue(Array.from(event.dataTransfer.files))
}

// 移除文件
const removeFile = (index: number) => {
  if (!files.value) return
  
  // 过滤掉要删除的文件
  files.value = files.value.filter((_, i) => i !== index)
  
  // 如果没有文件了，清空文件列表
  if (files.value.length === 0) {
    files.value = null
  }
  
  // 更新父组件的文件列表
  emit('update:files', files.value)
  
  // 清除错误提示
  error.value = ''
}

// 处理关闭
const handleClose = () => {
  if (uploading.value) return
  dialogVisible.value = false
  files.value = null
  emit('update:files', null)
}

// 处理上传
const handleUpload = async () => {
  if (!files.value || files.value.length === 0) {
    error.value = '请选择要上传的文件'
    return
  }
  
  if (!files.value.every(file => isValidFileType(file))) {
    error.value = '存在不支持的文件格式'
    return
  }
  
  uploading.value = true
  error.value = ''
  uploadProgress.value = 0
  uploadedCount.value = 0
  totalCount.value = files.value.length
  
  try {
    console.log('开始上传文件:', files.value)
    // 使用新的批量上传接口
    const uploadedUrls = await uploadApi.uploadFiles(
      files.value,
      (progress) => {
        uploadProgress.value = progress
        console.log('总体上传进度:', progress)
      },
      (fileIndex, progress) => {
        // 当一个文件完成上传时更新计数
        if (progress === 100) {
          uploadedCount.value = fileIndex + 1
        }
        console.log(`文件 ${files.value[fileIndex].name} 上传进度: ${progress}%`)
      }
    )
    
    // var newUploadedUrls = []
    // for (let i = 0; i < uploadedUrls.length; i++) {
    //   newUploadedUrls.push({
    //     url: uploadedUrls[i],
    //     name: files.value[i].name
    //   })
    // }
    console.log('文件上传完成，URLs:', uploadedUrls)
    // 发送上传完成的事件
    emit('upload', uploadedUrls)
    // emit('upload', newUploadedUrls)
    
    // 清理状态
    files.value = null
    dialogVisible.value = false
    
  } catch (err: any) {
    console.error('上传过程发生错误:', err)
    error.value = err.message || '上传失败，请重试'
  } finally {
    uploading.value = false
    uploadProgress.value = 0
    uploadedCount.value = 0
    totalCount.value = 0
  }
}
</script>

<style scoped>
.upload-area {
  border: 2px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.upload-area:hover {
  border-color: var(--v-primary-base);
  background: rgba(var(--v-primary-base), 0.05);
}

.hidden-input {
  display: none;
}

.file-list {
  border-radius: 8px;
  overflow: hidden;
  background-color: #3A3B3F !important;
}

.file-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
}

.progress-container {
  background: #3A3B3F;
  border-radius: 8px;
  padding: 16px;
}

.progress-info {
  flex: 1;
}

.waiting-files {
  font-size: 0.875rem;
  opacity: 0.7;
}

:deep(.v-card-text) {
  color: white !important;
}
.text-body-2{
  color:rgba(255, 255, 255, 0.26) !important
}
.v-list-item-title{
  color:rgba(255, 255, 255, 0.7) !important
}
.v-list-item-subtitle{
  color:rgba(255, 255, 255, 0.26) !important
}
</style>